import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [deliveryFee, setDeliveryFee] = useState(50);
  const [freeDeliveryMinimum, setFreeDeliveryMinimum] = useState(750);

  // LocalStorage'dan sepeti yükle
  useEffect(() => {
    const saved = localStorage.getItem('misfirin_cart');
    if (saved) {
      setCartItems(JSON.parse(saved));
    }
  }, []);

  // Sepet değişince kaydet
  useEffect(() => {
    localStorage.setItem('misfirin_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Paket servis durumu
  const [deliveryActive, setDeliveryActive] = useState(true);
  const [deliveryClosedReason, setDeliveryClosedReason] = useState('');

  // Teslimat bilgilerini yükle
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/delivery-info`)
      .then(res => res.json())
      .then(data => {
        setDeliveryFee(data.delivery_fee || 50);
        setFreeDeliveryMinimum(data.free_delivery_minimum || 750);
        setDeliveryActive(data.is_active ?? true);
        setDeliveryClosedReason(data.closed_reason || '');
      })
      .catch(err => console.error('Teslimat bilgisi alınamadı:', err));
  }, []);

  // Sepete ürün ekle (gram destekli)
  // product: { id, name, price, sales_unit, selectedGram (kg ürünleri için) }
  const addToCart = (product, selectedGram = null) => {
    setCartItems(prev => {
      // Kg bazlı ürünler için benzersiz ID oluştur (ürün id + gram)
      const cartItemId = product.sales_unit === 'kg' 
        ? `${product.id}_${selectedGram}` 
        : product.id;
      
      const existing = prev.find(item => item.cartItemId === cartItemId);
      
      if (existing) {
        return prev.map(item =>
          item.cartItemId === cartItemId
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      
      // Fiyat hesapla
      let itemPrice = product.price;
      if (product.sales_unit === 'kg' && selectedGram) {
        itemPrice = (product.price / 1000) * selectedGram;
      }
      
      return [...prev, { 
        ...product, 
        cartItemId,
        selectedGram,
        calculatedPrice: itemPrice,
        quantity: 1 
      }];
    });
  };

  // Miktarı artır
  const increaseQuantity = (cartItemId) => {
    setCartItems(prev =>
      prev.map(item =>
        item.cartItemId === cartItemId
          ? { ...item, quantity: item.quantity + 1 }
          : item
      )
    );
  };

  // Miktarı azalt
  const decreaseQuantity = (cartItemId) => {
    setCartItems(prev =>
      prev.map(item =>
        item.cartItemId === cartItemId && item.quantity > 1
          ? { ...item, quantity: item.quantity - 1 }
          : item
      ).filter(item => item.quantity > 0)
    );
  };

  // Sepetten ürün çıkar
  const removeFromCart = (cartItemId) => {
    setCartItems(prev => prev.filter(item => item.cartItemId !== cartItemId));
  };

  // Sepeti temizle
  const clearCart = () => {
    setCartItems([]);
  };

  // Toplam hesapla (gram fiyatlarıyla)
  const subtotal = cartItems.reduce((sum, item) => {
    const price = item.calculatedPrice || item.price;
    return sum + (price * item.quantity);
  }, 0);
  
  const calculatedDeliveryFee = subtotal >= freeDeliveryMinimum ? 0 : deliveryFee;
  const total = subtotal + calculatedDeliveryFee;
  const itemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      increaseQuantity,
      decreaseQuantity,
      removeFromCart,
      clearCart,
      subtotal,
      deliveryFee: calculatedDeliveryFee,
      freeDeliveryMinimum,
      total,
      itemCount,
      deliveryActive,
      deliveryClosedReason
    }}>
      {children}
    </CartContext.Provider>
  );
};

// Sepet Bileşeni (Floating Cart Button + Drawer)
export const CartButton = ({ onClick }) => {
  const { itemCount, total } = useCart();
  
  if (itemCount === 0) return null;
  
  return (
    <button
      onClick={onClick}
      style={{
        position: 'fixed',
        bottom: '100px',
        right: '20px',
        backgroundColor: '#22c55e',
        color: 'white',
        padding: '15px 25px',
        borderRadius: '50px',
        border: 'none',
        boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '16px',
        fontWeight: 'bold',
        zIndex: 10001
      }}
    >
      <span style={{ fontSize: '20px' }}>🛒</span>
      <span>{itemCount} ürün</span>
      <span style={{ backgroundColor: 'rgba(255,255,255,0.2)', padding: '5px 10px', borderRadius: '20px' }}>
        {total.toFixed(2)}₺
      </span>
    </button>
  );
};

// Ürün adını gram bilgisiyle formatla
const formatProductName = (item) => {
  if (item.sales_unit === 'kg' && item.selectedGram) {
    return `${item.name} – ${item.selectedGram} gr`;
  }
  return item.name;
};

// Sepet Drawer
export const CartDrawer = ({ isOpen, onClose }) => {
  const { 
    cartItems, 
    increaseQuantity, 
    decreaseQuantity, 
    removeFromCart, 
    clearCart,
    subtotal, 
    deliveryFee, 
    freeDeliveryMinimum,
    total 
  } = useCart();

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 2000
    }}>
      {/* Overlay */}
      <div 
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)'
        }}
      />
      
      {/* Drawer */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        maxWidth: '450px',
        backgroundColor: 'white',
        boxShadow: '-4px 0 20px rgba(0,0,0,0.2)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#d97706',
          color: 'white'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>🛒 Sepetim</h2>
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '5px'
            }}
          >
            ✕
          </button>
        </div>

        {/* Cart Items */}
        <div style={{ flex: 1, overflow: 'auto', padding: '15px' }}>
          {cartItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              <p style={{ fontSize: '48px', marginBottom: '15px' }}>🛒</p>
              <p>Sepetiniz boş</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {cartItems.map(item => (
                <div 
                  key={item.cartItemId}
                  style={{
                    padding: '15px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600' }}>
                      {formatProductName(item)}
                    </h4>
                    <p style={{ margin: 0, color: '#d97706', fontWeight: 'bold' }}>
                      {((item.calculatedPrice || item.price) * item.quantity).toFixed(2)}₺
                    </p>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      onClick={() => decreaseQuantity(item.cartItemId)}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        border: '1px solid #d97706',
                        backgroundColor: 'white',
                        color: '#d97706',
                        fontSize: '18px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      −
                    </button>
                    <span style={{ 
                      minWidth: '30px', 
                      textAlign: 'center', 
                      fontWeight: 'bold',
                      fontSize: '16px'
                    }}>
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => increaseQuantity(item.cartItemId)}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        border: 'none',
                        backgroundColor: '#d97706',
                        color: 'white',
                        fontSize: '18px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      +
                    </button>
                    <button
                      onClick={() => removeFromCart(item.cartItemId)}
                      style={{
                        marginLeft: '8px',
                        background: 'none',
                        border: 'none',
                        color: '#ef4444',
                        fontSize: '18px',
                        cursor: 'pointer'
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer - Totals & Checkout */}
        {cartItems.length > 0 && (
          <div style={{
            padding: '20px',
            borderTop: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb'
          }}>
            {/* Ücretsiz teslimat mesajı */}
            {subtotal < freeDeliveryMinimum && (
              <div style={{
                padding: '10px 15px',
                backgroundColor: '#fef3c7',
                borderRadius: '8px',
                marginBottom: '15px',
                fontSize: '14px',
                color: '#92400e'
              }}>
                🚚 {(freeDeliveryMinimum - subtotal).toFixed(2)}₺ daha ekleyin, teslimat ücretsiz!
              </div>
            )}
            
            <div style={{ marginBottom: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>Ara Toplam:</span>
                <span style={{ fontWeight: '600' }}>{subtotal.toFixed(2)}₺</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>Teslimat:</span>
                <span style={{ fontWeight: '600', color: deliveryFee === 0 ? '#22c55e' : '#111' }}>
                  {deliveryFee === 0 ? 'ÜCRETSİZ' : `${deliveryFee.toFixed(2)}₺`}
                </span>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                paddingTop: '10px',
                borderTop: '1px solid #e5e7eb',
                fontSize: '18px',
                fontWeight: 'bold'
              }}>
                <span>Toplam:</span>
                <span style={{ color: '#d97706' }}>{total.toFixed(2)}₺</span>
              </div>
            </div>

            <a
              href="/siparis"
              style={{
                display: 'block',
                width: '100%',
                padding: '15px',
                backgroundColor: '#22c55e',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                textAlign: 'center',
                textDecoration: 'none',
                boxSizing: 'border-box'
              }}
            >
              Siparişi Tamamla →
            </a>

            <button
              onClick={clearCart}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: 'transparent',
                color: '#ef4444',
                border: 'none',
                fontSize: '14px',
                cursor: 'pointer',
                marginTop: '10px'
              }}
            >
              Sepeti Temizle
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartContext;
